import { useState } from 'react';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQContent {
  title?: string;
  items?: FAQItem[];
}

interface FAQSectionProps {
  content?: FAQContent;
}

const defaultFAQs: FAQItem[] = [
  {
    q: 'What part fixes broken blinds?',
    a: 'It depends on what is broken. If slats are missing or cracked, you need replacement slats or vanes. If the blinds won\'t lift or lower, the lift cord or cord lock likely needs replacing. If the blind falls off the wall, check the mounting brackets. If it won\'t tilt, the tilt mechanism or wand may be the issue.',
  },
  {
    q: 'How do I replace a blind cord?',
    a: 'Remove the blind from the window and lay it flat. Untie the old cord from the bottom rail and thread it back up through the slat holes and cord lock. Tie the new cord to the old one and pull it through. Secure the new cord with a knot at the bottom, replace the tassel, and rehang. Most repairs take under 20 minutes.',
  },
  {
    q: 'Where can I buy blind replacement parts?',
    a: 'Blinds USA Inc stocks over 1,000 replacement parts for vertical blinds, mini blinds, roller shades, and drapery hardware. We ship nationwide with free shipping on orders over $49. Browse our full catalog online or call our parts specialists for help identifying the exact component you need.',
  },
  {
    q: 'Do you carry parts for all blind brands?',
    a: 'We carry universal-fit parts compatible with most major brands including Levolor, Hunter Douglas, Bali, Graber, and Springs Window Fashions. Many blind hardware components are industry-standard sized, meaning one part fits across dozens of brands and models. Call us for help matching discontinued or specialty parts.',
  },
  {
    q: 'How long does shipping take?',
    a: 'Most orders are processed within 1–2 business days and shipped via standard ground service. Delivery typically takes 3–7 business days depending on your location. Expedited shipping is available at checkout. We ship to all 50 states from our central warehouse.',
  },
  {
    q: 'What is the minimum order?',
    a: 'There is no minimum order requirement. Many parts are sold individually, which is ideal when you only need one replacement slat or a single bracket. We also offer bulk pricing for property managers and installers who need larger quantities. Free shipping kicks in at $49.',
  },
];

export default function FAQSection({ content }: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(null);
  const title = content?.title || 'Frequently Asked Questions';
  const items = content?.items || defaultFAQs;

  return (
    <section className="bg-white py-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-amber-600 text-xs font-bold tracking-widest uppercase mb-3">
            FAQ
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-stone-900">{title}</h2>
          <p className="text-stone-500 mt-3 text-base">
            Quick answers to common questions about blind parts, ordering, and shipping.
          </p>
        </div>

        <div className="space-y-3">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="border border-stone-200 rounded-lg overflow-hidden"
              itemScope
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpen(open === idx ? null : idx)}
                className="w-full flex items-start justify-between gap-4 px-6 py-5 bg-white hover:bg-stone-50 transition-colors cursor-pointer text-left"
              >
                <h3
                  className="font-semibold text-stone-800 text-sm leading-snug pr-2"
                  itemProp="name"
                >
                  {item.q}
                </h3>
                <i
                  className={`text-stone-400 text-lg flex-shrink-0 transition-transform duration-200 mt-0.5 ${
                    open === idx ? 'ri-subtract-line text-amber-600' : 'ri-add-line'
                  }`}
                />
              </button>
              {open === idx && (
                <div
                  className="px-6 pb-6 bg-stone-50 border-t border-stone-100"
                  itemScope
                  itemType="https://schema.org/Answer"
                  itemProp="acceptedAnswer"
                >
                  <p
                    className="text-stone-600 text-sm leading-relaxed mt-4"
                    itemProp="text"
                  >
                    {item.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-stone-500 text-sm">
            Still have questions?{' '}
            <a
              href="tel:+18005551234"
              className="text-amber-700 font-semibold hover:text-amber-600 cursor-pointer"
            >
              Call our parts specialists at 1-800-555-1234
            </a>
          </p>
        </div>
      </div>

      {/* Schema.org FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          }),
        }}
      />
    </section>
  );
}
